import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import fetchWithAuth from '../api/fetchWithAuth';
import apiUrls from '../api/apiUrls';
import { useLang } from ".././contexts/LanguageContext";
import { useAmbient } from '../contexts/AmbientContext';
import Loader from '../components/Loader';
import ChapterMenu from '../components/Chapter/ChapterMenu';
import LemmaModal from '../components/Chapter/LemmaModal';
import Transcription from '../components/Chapter/Transcription';

const Chapter = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { setCurrentTrackId, setVolume, setIsPlaying } = useAmbient();

  const [searchParams] = useSearchParams();
  const chapterHash = searchParams.get('id');
  const legacyBookId = searchParams.get('book_id');
  const legacyChapterId = searchParams.get('chapter_id');

  const [chapterData, setChapterData] = useState(null);
  const [audioData, setAudioData] = useState(null);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState({ langOrigen: '', dictLanguage: '', lemmaId: '', lemmaInfor: '' });
  const [showModal, setShowModal] = useState(false);
  const [currentWordId, setCurrentWordId] = useState(-1);
  const [fontSize, setFontSize] = useState(2); // Default font size
  const [showLemma, setShowLemma] = useState(false); // Default showLemma
  const [dictLanguage, setDictLanguage] = useState('esp'); // Default dictoinary language

  // Move loadAudioData here to make it accessible globally within the component
  const loadAudioData = async (dialectCode, hashOrBookId, chapterId = null) => {
    try {
      const targetUrl = chapterId 
        ? apiUrls.chapterAudioApi(dialectCode, hashOrBookId, chapterId)
        : apiUrls.chapterAudioApi(dialectCode, hashOrBookId);

      const response = await fetchWithAuth(targetUrl);
      if (!response.ok) {
        setError(`Audio API Error: ${response.status}`);
        return;
      }
      const result = await response.json();

      if (result && result.data) {
        setAudioData(result.data);
      } else {
        setError(result?.error || 'Error al obtener datos de audio (data null)');
      }
    } catch (err) {
      setError(t('CHAPTER_ERROR_GETTING_DATA'));
      console.error("[Chapter] Error en loadAudioData:", err);
    }
  };

  useEffect(() => {
    if (!chapterHash && (!legacyBookId || !legacyChapterId)) {
      return;
    }

    const loadChapter = async () => {
      try {
        const targetUrl = chapterHash 
          ? apiUrls.chapterApi(chapterHash)
          : apiUrls.chapterApi(legacyBookId, legacyChapterId);

        const response = await fetchWithAuth(targetUrl);
        if (!response.ok) { 
          setError(`Chapter API Error: ${response.status}`); 
          return; 
        }
        const result = await response.json();
        
        if (result && result.data) {
          setChapterData(result.data);
          
          // Inicializar preferencias de audio ambiental
          if (result.data.preferences) {
            if (result.data.preferences.ambient_track_id) {
              setCurrentTrackId(result.data.preferences.ambient_track_id);
              setIsPlaying(true);
            }
            if (result.data.preferences.ambient_volume !== undefined) {
              setVolume(parseFloat(result.data.preferences.ambient_volume));
            }
          }

          setFontSize(result.data.preferences?.font_size !== undefined ? result.data.preferences.font_size : 2);
          setShowLemma(result.data.preferences?.show_IPA !== undefined ? Boolean(Number(result.data.preferences.show_IPA)) : true);
          setDictLanguage(
            result.data.preferences?.dict_language ?? 'esp'
          );
          
          const currentHash = chapterHash || result.data.chapter_hash;
          // Si tenemos chapterHash, no pasamos el cid (segundo argumento nulo en loadAudioData)
          await loadAudioData(result.data.preferences?.dialect_code || 'es-ES', currentHash, chapterHash ? null : legacyChapterId);
        } else {
          setError(result?.error || t('CHAPTER_UNKNOWN_ERROR_GETTING_DATA'));
        }
      } catch (err) {
        setError(t('CHAPTER_ERROR_GETTING_DATA'));
        console.error("[Chapter] Error en loadChapter:", err);
      }
    };

    loadChapter();
  }, [chapterHash, legacyBookId, legacyChapterId]);

  const handleDialectChange = async (newDialect) => {
    const audio = document.querySelector('audio');
    const bid = chapterData.book_id;
    const cid = chapterData.chapter_id;
    const formattedDialect = newDialect.replace('-', '_');
    const newAudioUrl = `${apiUrls.audioFiles}${bid}/${bid}_${cid}_${formattedDialect}.mp3`;
    audio.src = newAudioUrl;
  
    try {
      const currentHash = chapterHash || chapterData.chapter_hash;
      await loadAudioData(newDialect, currentHash); // Load new audio data for the selected dialect
      audio.addEventListener(
        'canplaythrough',
        () => {
          audio.play()
            .then(() => {
              const playPauseButton = document.getElementById('playPauseButton');
              if (playPauseButton) {
                playPauseButton.classList.add('pause');
                playPauseButton.classList.remove('play');
              }
            })
            .catch((error) => {
              console.error(t('CHAPTER_ERROR_AUTOPLAY_AUDIO'), error);
            });
        },
        { once: true }
      );
    } catch (err) {
      console.error(t('CHAPTER_ERROR_LOADING_AUDIO_DATA_FOR_NEW_DIALECT'), err);
    }
  };

  if (error) return <p>ERROR: {error}</p>;
  if (!chapterData || !audioData) return <Loader visible={true}/>;

  const { preferences, chapter_id, chapter_name, book_short_title, book_print_date, chap_next, chap_prev, chapter_lemmatization, book_id } = chapterData;
  
  const updatePlayPauseButton = (isPlaying) => {
    const playPauseButton = document.getElementById('playPauseButton');
    if (!playPauseButton) return;
    if (isPlaying) {
      playPauseButton.classList.add('pause');
      playPauseButton.classList.remove('play');
    } else {
      playPauseButton.classList.add('play');
      playPauseButton.classList.remove('pause');
    }
  };

  const handleWordClick = (lemma, pos, id) => {
    const effectiveDictLanguage =
      dictLanguage ?? chapterData?.preferences?.dict_language ?? 'esp';
    setModalData({
      langOrigen: chapterData.book_language,
      dictLanguage: effectiveDictLanguage,
      lemmaId: lemma,
      lemmaInfor: pos,
    });
    setShowModal(true);
    const audioElement = document.querySelector('audio');
    if (audioElement && !audioElement.paused) {
      audioElement.pause();
      updatePlayPauseButton(false);
    }
  };

  const handleCloseModal = (resumeAudio = false) => {
    setShowModal(false);
    const shouldResume = typeof resumeAudio === 'boolean' ? resumeAudio : false;
    if (shouldResume) {
      const audioElement = document.querySelector('audio');
      if (audioElement) {
        audioElement
          .play()
          .then(() => updatePlayPauseButton(true))
          .catch((err) => {
            console.error('Error al reanudar el audio:', err);
          });
      }
    }
  };

  const parsedTime = typeof audioData.chapter_time === 'string' ? JSON.parse(audioData.chapter_time) : audioData.chapter_time;
  const wordTimings = parsedTime?.words || [];
  // Actualizamos los índices de las palabras porque la API entrega un id que no es consecutivo
  const updatedWordTimings = wordTimings.map((word, index) => {
    const { id, ...rest } = word; // Eliminamos la propiedad id
    return {
      id: index + 1, // Reemplazamos la propiedad id con el Í­ndice + 1
      ...rest
    };
  });

  const phonologizationArray = typeof audioData.chapter_phonologization === 'string' 
    ? JSON.parse(audioData.chapter_phonologization) 
    : audioData.chapter_phonologization;

  const handleWordChange = (currentWordId) => {
    setCurrentWordId(currentWordId);
  };

  const handleFontSizeChange = (newFontSize) => {
    setFontSize(newFontSize);
  };

  const handleShowLemmaChange = (newShowLemma) => {
    setShowLemma(newShowLemma);
  };

  const handleDictLanguageChange = (newDictLanguage) => {
    setDictLanguage(newDictLanguage);
  }

  const handleLastWordReached = () => {
    if (chapterData.chap_next_hash) {
      setTimeout(() => {
        navigate(`/chapter?id=${chapterData.chap_next_hash}`);
      }, 500);
    } else if (chapterData.chap_next) {
      setTimeout(() => {
        navigate(`/chapter?book_id=${book_id}&chapter_id=${chapterData.chap_next}`);
      }, 500);
    } else {
      console.log("This is the End.");
    }
  };

  const handleAutoPlayChange = (newAutoPlay) => {
    chapterData.preferences.auto_play = newAutoPlay ? 1 : 0; // Update the local preferences
  };

  return (
    <div className="background_reader">

      <ChapterMenu 
        bookId={book_id} 
        chapterId={chapter_id}
        bookLanguage={chapterData.book_language}
        chapterName={chapter_name}
        bookShortTitle={book_short_title}
        bookPrintDate={book_print_date}
        previousChapterId={chap_prev}
        nextChapterId={chap_next}
        previousChapterHash={chapterData.chap_prev_hash}
        nextChapterHash={chapterData.chap_next_hash}
        preferences={preferences}
        wordTimings={updatedWordTimings}
        onWordChange={handleWordChange}
        onFontSizeChange={handleFontSizeChange} // Pass the function to ChapterMenu
        onShowLemmaChange={handleShowLemmaChange} // Pass the function to ChapterMenu
        onDictLanguageChange={handleDictLanguageChange} // Pass the function to ChapterMenu
        onLastWordReached={handleLastWordReached} // Pass the callback to ChapterMenu
        autoPlay={preferences.auto_play === 1} // Pass autoPlay preference
        onAutoPlayChange={handleAutoPlayChange} // Pass the handler to ChapterMenu
        onDialectChange={handleDialectChange} // Pass the handler to ChapterMenu
      />

      <main role="main">
        <div className="container transcription-container">
          <div id="transcription" className="uTextC">            
            <Transcription
              lemmatizationArray={chapter_lemmatization}
              phonologizationArray={phonologizationArray}
              handleWordClick={handleWordClick}
              currentWordId={currentWordId}
              fontSize={fontSize} // Pass the fontSize to Transcription
              showLemma={showLemma} // Pass the showLemma to Transcription
            />
            <LemmaModal
              show={showModal}
              handleClose={handleCloseModal}
              langOrigen={modalData.langOrigen}
              dictLanguage={modalData.dictLanguage}
              lemmaId={modalData.lemmaId}
              lemmaInfor={modalData.lemmaInfor}
            />
          </div>
        </div>
      </main>
      
    </div>
  );
};

export default Chapter;
